import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import {
    LayoutDashboard, Users, UserCog, Settings, LogOut, ChevronLeft, ChevronRight, Building2, Menu, X, FileText, GitCompare
} from "lucide-react";
import { useState, useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46ba8d0f-50d1-45b0-932d-164e40ecbfdf/artifacts/l81b46nl_K2B_Logo_Final-2.png";

export default function Sidebar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Close mobile menu on route change
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

    const adminLinks = [
        { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
        { path: "/admin/coaches", label: "Coaches", icon: UserCog },
        { path: "/admin/users", label: "All Users", icon: Users },
        { path: "/admin/org-reports", label: "Org Reports", icon: Building2 },
        { path: "/admin/coach-comparison", label: "Coach Comparison", icon: GitCompare },
        { path: "/admin/versions", label: "Versions", icon: FileText },
    ];

    const coachLinks = [
        { path: "/coach", label: "Dashboard", icon: LayoutDashboard },
        { path: "/coach/users", label: "My Users", icon: Users },
    ];

    const links = user?.impersonating
        ? (user?.role === "coach" ? coachLinks : [])
        : (user?.role === "admin" ? adminLinks : coachLinks);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const handleNav = (path) => {
        navigate(path);
        setMobileOpen(false);
    };

    const sidebarContent = (
        <>
            <div className="flex items-center gap-3 p-4 h-16">
                <img src={LOGO_URL} alt="K2B Learning" className="h-8 w-8 object-contain flex-shrink-0" />
                {(!collapsed || mobileOpen) && (
                    <span className="font-semibold text-sm text-foreground truncate" style={{ fontFamily: "Outfit" }}>
                        K2B Learning
                    </span>
                )}
                {/* Mobile close button */}
                <button className="ml-auto md:hidden text-muted-foreground" onClick={() => setMobileOpen(false)}>
                    <X className="h-5 w-5" />
                </button>
            </div>
            <Separator />
            <nav className="flex-1 p-2 space-y-1">
                {links.map(({ path, label, icon: Icon }) => (
                    <Button
                        key={path}
                        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        variant={isActive(path) ? "secondary" : "ghost"}
                        className={`w-full justify-start gap-3 ${isActive(path) ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"} ${collapsed && !mobileOpen ? "px-3" : ""}`}
                        onClick={() => handleNav(path)}
                    >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        {(!collapsed || mobileOpen) && <span className="text-sm">{label}</span>}
                    </Button>
                ))}
            </nav>
            <Separator />
            <div className="p-2 space-y-1">
                <Button
                    data-testid="nav-settings"
                    variant="ghost"
                    className={`w-full justify-start gap-3 text-muted-foreground hover:text-foreground ${collapsed && !mobileOpen ? "px-3" : ""}`}
                    onClick={() => {
                        const base = user?.impersonating ? (user?.role === "coach" ? "/coach" : "/admin") : (user?.role === "admin" ? "/admin" : "/coach");
                        handleNav(`${base}/settings`);
                    }}
                >
                    <Settings className="h-4 w-4 flex-shrink-0" />
                    {(!collapsed || mobileOpen) && <span className="text-sm">Settings</span>}
                </Button>
                <Button
                    data-testid="logout-btn"
                    variant="ghost"
                    className={`w-full justify-start gap-3 text-muted-foreground hover:text-destructive ${collapsed && !mobileOpen ? "px-3" : ""}`}
                    onClick={handleLogout}
                >
                    <LogOut className="h-4 w-4 flex-shrink-0" />
                    {(!collapsed || mobileOpen) && <span className="text-sm">Logout</span>}
                </Button>
            </div>
            <Separator className="hidden md:block" />
            <div className="p-2 hidden md:block">
                <Button variant="ghost" size="sm" className="w-full" onClick={() => setCollapsed(!collapsed)}>
                    {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
            </div>
        </>
    );

    return (
        <>
            {/* Mobile top bar */}
            <div data-testid="mobile-header" className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center gap-3 h-14 px-4 bg-card border-b border-border">
                <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="text-foreground">
                    <Menu className="h-5 w-5" />
                </button>
                <img src={LOGO_URL} alt="K2B Learning" className="h-7 w-7 object-contain" />
                <span className="font-semibold text-sm text-foreground" style={{ fontFamily: "Outfit" }}>K2B Learning</span>
            </div>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
                    <aside className="relative flex flex-col h-full w-64 bg-card border-r border-border z-10 animate-slide-in">
                        {sidebarContent}
                    </aside>
                </div>
            )}

            {/* Desktop sidebar */}
            <aside
                data-testid="sidebar"
                className={`hidden md:flex flex-col h-screen border-r border-border bg-card transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}
            >
                {sidebarContent}
            </aside>
        </>
    );
}
